import React from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap'
import { TextField, Autocomplete, Switch, FormControlLabel, IconButton, Tooltip, Box } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import TinyEditor from '../Editor/TinyEditor'
import SportIcon from '../Icons/SportIcon'
import { DEFAULT_SPORTS, DEFAULT_TYPES } from '../../sports'

const ALL_OPTION = { key: 'all', label: 'Kaikki' }

// Placeholders the sender substitutes into the subject and body. These are
// activity fields, unlike the {RESERVATION_*} set the reservation confirmations use.
const DYNAMIC_FIELDS = [
  { token: 'PERSON_NAME', description: 'Asiakkaan etu- ja sukunimi' },
  { token: 'ACTIVITY_TITLE', description: 'Aktiviteetin nimi' },
  { token: 'ACTIVITY_START_TIME', description: 'Aktiviteetin alkuaika' },
  { token: 'ACTIVITY_SPORT', description: 'Laji' },
  { token: 'ACTIVITY_TYPE', description: 'Tyyppi' },
  { token: 'ACTIVITY_DESCRIPTION', description: 'Kuvaus' },
  { token: 'ACTIVITY_PRICE', description: 'Hinta' },
]

const renderIconOption = (props, option) => (
  <Box component='li' sx={{ display: 'flex', gap: 1, alignItems: 'center' }} {...props}>
    {option.key === 'all' ? null : (
      <SportIcon name={option.icon} icontype={option.icontype} fontSize='small' />
    )}
    {option.label}
  </Box>
)

const INIT_TEMPLATE = {
  collection: 'activity',
  activitytype: 'all',
  sport: 'all',
  venue: 'all',
  lang: 'all',
  channel: 'mail',
  memberships: [],
  enabled: true,
  default: false,
  message: {
    subject: '',
    body: '',
    htmlbody: '',
  },
}

function withoutEmpty(item) {
  return Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined && v !== null && v !== ''))
}

// Merges the customer specific list from config with the built in defaults.
// The override is applied field by field: a customer entry that only renames a sport
// carries no icon, and replacing the whole entry would drop the built in one.
function mergeOptions(defaults, list) {
  const byKey = new Map(defaults.map((d) => [d.key, d]))
  ;(list || []).forEach((item) => {
    if (!item || !item.key) return
    byKey.set(item.key, { ...(byKey.get(item.key) || {}), ...withoutEmpty(item) })
  })
  const unique = [...byKey.values()].sort((a, b) => (a.label || '').localeCompare(b.label || ''))
  return [ALL_OPTION, ...unique]
}

// Keeps a stored key selectable even if it has since been dropped from the config lists,
// so an old template does not silently read as 'Kaikki'.
function optionsWith(options, key) {
  if (!key || options.some((o) => o.key === key)) return options
  return [...options, { key, label: key }]
}

function combinationKey(template) {
  return (template.activitytype || 'all') + '|' + (template.sport || 'all')
}

// Indexes of every row that shares its activitytype + sport combination with another row.
function findDuplicateIndexes(templates) {
  const counts = {}
  templates.forEach((t) => {
    const k = combinationKey(t)
    counts[k] = (counts[k] || 0) + 1
  })
  const duplicates = new Set()
  templates.forEach((t, i) => {
    if (counts[combinationKey(t)] > 1) duplicates.add(i)
  })
  return duplicates
}

function ActivityConfirmationMessageRow(props) {
  const {
    index: i,
    template,
    activitytypeOptions,
    sportOptions,
    isDuplicate,
    openEditMessageModal,
    updateTemplateName,
    updateTemplateActivitytype,
    updateTemplateSport,
    updateTemplateEnabled,
    removeTemplate,
  } = props

  const typeOptions = optionsWith(activitytypeOptions, template.activitytype)
  const lajiOptions = optionsWith(sportOptions, template.sport)

  return (
    <Row style={{ borderTop: '1px solid rgba(0, 0, 0, 0.10)', paddingTop: 15, paddingBottom: 5 }}>
      <Col md='3' xs='12'>
        <Autocomplete
          id={'activitytype.' + i}
          options={typeOptions}
          getOptionLabel={(o) => o.label || ''}
          isOptionEqualToValue={(o, v) => o.key === v.key}
          value={typeOptions.find((x) => x.key === (template.activitytype || 'all')) || ALL_OPTION}
          onChange={(_e, val) => updateTemplateActivitytype(val || ALL_OPTION)}
          renderOption={renderIconOption}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} label='Tyyppi' variant='standard' error={isDuplicate} fullWidth />
          )}
        />
      </Col>
      <Col md='3' xs='12'>
        <Autocomplete
          id={'sport.' + i}
          options={lajiOptions}
          getOptionLabel={(o) => o.label || ''}
          isOptionEqualToValue={(o, v) => o.key === v.key}
          value={lajiOptions.find((x) => x.key === (template.sport || 'all')) || ALL_OPTION}
          onChange={(_e, val) => updateTemplateSport(val || ALL_OPTION)}
          renderOption={renderIconOption}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} label='Laji' variant='standard' error={isDuplicate} fullWidth />
          )}
        />
      </Col>
      <Col md='2' xs='12'>
        <FormControlLabel
          control={
            <Switch
              checked={template.enabled || false}
              onChange={(e) => updateTemplateEnabled(e.target.checked)}
              color='primary'
              id={'enabled.' + i}
            />
          }
          label='Käytössä'
        />
      </Col>
      <Col md='2' xs='12' style={{ paddingRight: 30 }}>
        <TextField
          fullWidth
          id={'displayName.' + i}
          label='Nimi'
          value={template.displayName ? template.displayName : ''}
          onChange={updateTemplateName}
        />
      </Col>
      <Col>
        <Button color={'success'} onClick={() => openEditMessageModal(i)}>
          Viesti
        </Button>
        <Tooltip title='Poista viesti'>
          <IconButton onClick={() => removeTemplate(i)} size='large'>
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </Col>
      {isDuplicate ? (
        <Col md='12'>
          <small className='text-danger'>
            Tyypin ja lajin yhdistelmä on jo käytössä toisessa viestissä.
          </small>
        </Col>
      ) : null}
    </Row>
  )
}

class ActivityConfirmationMessageTemps extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      activityconfirmations: [],
      activitytypeOptions: [ALL_OPTION],
      sportOptions: [ALL_OPTION],
      loadingTemplates: true,
      saved: true,
      showMessageModal: false,
      selectedIndex: -1,
      showDynamicHelp: false,
    }
  }

  componentDidMount() {
    this.unsubscribe = this.props.firebase.getMessageTemplates().onSnapshot((doc) => {
      this.setState({
        activityconfirmations: (doc.exists && doc.data().activityconfirmations) || [],
        loadingTemplates: false,
      })
    })
    this.unsubscribeTypes = this.props.firebase.getActivitytypes().onSnapshot((doc) => {
      this.setState({
        activitytypeOptions: mergeOptions(DEFAULT_TYPES, doc.exists ? doc.data().list : null),
      })
    })
    this.unsubscribeSports = this.props.firebase.getSports().onSnapshot((doc) => {
      this.setState({
        sportOptions: mergeOptions(DEFAULT_SPORTS, doc.exists ? doc.data().list : null),
      })
    })
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe()
    if (this.unsubscribeTypes) this.unsubscribeTypes()
    if (this.unsubscribeSports) this.unsubscribeSports()
  }

  addRow = () => {
    const j = [...this.state.activityconfirmations, JSON.parse(JSON.stringify(INIT_TEMPLATE))]
    this.setState({ activityconfirmations: j, saved: false })
  }

  removeRow = (index) => {
    if (!window.confirm('Poista viesti?')) return
    const j = this.state.activityconfirmations.filter((_, i) => i !== index)
    this.setState({ activityconfirmations: j, saved: false })
  }

  toggleMessage = () => {
    this.setState({ showMessageModal: !this.state.showMessageModal })
  }

  toggleDynamicHelp = () => {
    this.setState({ showDynamicHelp: !this.state.showDynamicHelp })
  }

  handleMessageBodyChange = (html) => {
    let j = this.state.activityconfirmations
    if (!j[this.state.selectedIndex]) return
    if (!j[this.state.selectedIndex].message) j[this.state.selectedIndex].message = { htmlbody: '', subject: '' }
    j[this.state.selectedIndex].message.htmlbody = html
    delete j[this.state.selectedIndex].message.body
    this.setState({ activityconfirmations: j, saved: false })
  }

  handleMessageSubjectChange = (e) => {
    let j = this.state.activityconfirmations
    if (!j[this.state.selectedIndex].message) j[this.state.selectedIndex].message = { body: '', subject: '' }
    j[this.state.selectedIndex].message.subject = e.target.value
    this.setState({ activityconfirmations: j, saved: false })
  }

  onSave = (event) => {
    event.preventDefault()
    const _rows = this.state.activityconfirmations.map((r) => {
      delete r.empty
      return r
    })
    this.props.firebase.addMessageTemplates({ activityconfirmations: _rows }).then(() => {
      this.setState({ saved: true })
    })
  }

  render() {
    if (this.state.loadingTemplates) return <></>
    const selected = this.state.activityconfirmations[this.state.selectedIndex]
    const duplicates = findDuplicateIndexes(this.state.activityconfirmations)
    return (
      <>
        <div className='content'>
          <Row>
            <Col md='12'>
              <Card>
                <CardHeader>
                  <Row>
                    <Col lg='10' md='5' sm='5'>
                      <CardTitle tag='h5'>Aktiviteettien vahvistusviestit</CardTitle>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  <p>
                    Viesti valitaan aktiviteetin tyypin ja lajin perusteella. Kaikki-valinta kelpaa mihin tahansa
                    tyyppiin tai lajiin. Samaa tyypin ja lajin yhdistelmää ei voi määritellä kahdesti.
                  </p>
                  {this.state.activityconfirmations.map((c, i) => (
                    <ActivityConfirmationMessageRow
                      key={i}
                      index={i}
                      template={c}
                      activitytypeOptions={this.state.activitytypeOptions}
                      sportOptions={this.state.sportOptions}
                      isDuplicate={duplicates.has(i)}
                      openEditMessageModal={() => {
                        this.setState({ selectedIndex: i })
                        this.toggleMessage()
                      }}
                      updateTemplateName={(e) => {
                        let j = this.state.activityconfirmations
                        j[i].displayName = e.target.value
                        this.setState({ activityconfirmations: j, saved: false })
                      }}
                      updateTemplateActivitytype={(selection) => {
                        let j = this.state.activityconfirmations
                        j[i].activitytype = selection.key
                        this.setState({ activityconfirmations: j, saved: false })
                      }}
                      updateTemplateSport={(selection) => {
                        let j = this.state.activityconfirmations
                        j[i].sport = selection.key
                        this.setState({ activityconfirmations: j, saved: false })
                      }}
                      updateTemplateEnabled={(value) => {
                        let j = this.state.activityconfirmations
                        j[i].enabled = value
                        this.setState({ activityconfirmations: j, saved: false })
                      }}
                      removeTemplate={this.removeRow}
                    />
                  ))}
                  <Row>
                    <Col style={{ marginTop: '20px' }}>
                      <Button className='btn-round btn-icon' color='primary' size='sm' onClick={this.addRow}>
                        <i className='fa fa-plus' />
                      </Button>{' '}
                      Lisää vahvistusviesti
                    </Col>
                  </Row>
                  <Row>
                    <Col className='ml-auto mr-auto' sm='12'>
                      <Button
                        disabled={this.state.saved || duplicates.size > 0}
                        color='primary'
                        onClick={this.onSave}
                      >
                        Tallenna muutokset
                      </Button>
                      {duplicates.size > 0 ? (
                        <span className='text-danger' style={{ marginLeft: 15 }}>
                          Korjaa päällekkäiset tyypin ja lajin yhdistelmät ennen tallennusta.
                        </span>
                      ) : null}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Message modal */}
          <Modal isOpen={this.state.showMessageModal} toggle={this.toggleMessage} size='lg'>
            <ModalHeader toggle={this.toggleMessage}>Viesti</ModalHeader>
            <ModalBody>
              <div>
                <Label for='messagetemplates'>Dynaamiset kentät</Label>
                {this.state.showDynamicHelp ? (
                  <KeyboardArrowUpIcon onClick={this.toggleDynamicHelp} />
                ) : (
                  <KeyboardArrowDownIcon onClick={this.toggleDynamicHelp} />
                )}
              </div>
              {this.state.showDynamicHelp ? (
                <div>
                  {DYNAMIC_FIELDS.map((f) => (
                    <Row key={f.token}>
                      <Col>&#123;{f.token}&#125;</Col>
                      <Col>{f.description}</Col>
                    </Row>
                  ))}
                </div>
              ) : null}

              <FormGroup>
                <Label for='messageSubject'>Otsikko</Label>
                <Input
                  id='messageSubject'
                  value={selected?.message?.subject || ''}
                  name='messageSubject'
                  onChange={this.handleMessageSubjectChange}
                />
              </FormGroup>
              <TinyEditor
                value={
                  selected?.message?.htmlbody
                    ? selected.message.htmlbody
                    : selected?.message?.body
                    ? selected.message.body.replace(/\n/g, '<br/>')
                    : ''
                }
                onChange={this.handleMessageBodyChange}
                onClose={this.handleMessageBodyChange}
                disableSave={true}
              />
            </ModalBody>
            <ModalFooter>
              <Button color='primary' onClick={this.toggleMessage}>
                Sulje
              </Button>
            </ModalFooter>
          </Modal>
        </div>
      </>
    )
  }
}

export default ActivityConfirmationMessageTemps
